import {
    AssignmentTurnedIn as AssignmentTurnedInIcon,
    Build as BuildIcon,
    Business as BusinessIcon,
    ConfirmationNumber as ConfirmationNumberIcon,
    Dns as DnsIcon,
    EventNote as ReleaseNoteIcon,
    Healing as SparePartsIcon,
    List as ListIcon,
    Notifications as NotificationsIcon,
    Person as PersonIcon,
    PieChart as PieChartIcon,
    Polyline as PolylineIcon,
    Settings as SettingsIcon,
    SettingsApplications as ClusterIcon,
} from '@mui/icons-material';
import { defineMessage } from 'react-intl';

import { apmsPermissions } from '../actions/collections/apms';
import { companiesPermissions } from '../actions/collections/companies';
import { jiraStatisticsPermissions } from '../actions/jiraStatistics';

export { default as TIMEZONES } from 'cleveron-web-components/static/timezones.json';

export const SEARCH_PATHS = {
    '/tickets': '/tickets/tickets',
};

// Shared by the menu entry below, the route in App/Routes, and the "only SC connections is
// available" check in ConnectedAppWrapper, so renaming the path cannot silently break the check.
export const SC_CONNECTIONS_PATH = 'sc-connections';

export const PATH_PERMISSIONS = [
    [
        {
            icon: ListIcon,
            path: 'apms',
            permissions: { 'cs-api': ['apm_read', 'series_read', 'company_read'] },
        },
        {
            icon: BuildIcon,
            path: 'maintenance',
            permissions: { 'cs-api': ['maintenance_issue_read'] },
        },
        {
            icon: AssignmentTurnedInIcon,
            path: 'installation',
            permissions: { 'cs-api': ['checklist_instance_read', 'checklist_read'] },
        },
        {
            icon: SparePartsIcon,
            path: 'spare-parts',
            permissions: { 'cs-api': ['part_change_order_read'] },
        },
        {
            icon: PolylineIcon,
            path: SC_CONNECTIONS_PATH,
            permissions: { 'cs-api': ['sc_connections_read'] },
        },
    ],
    [
        {
            icon: ConfirmationNumberIcon,
            path: 'tickets',
            permissions: { 'cs-api': ['issue_read'] },
        },
        {
            icon: PersonIcon,
            path: 'customer',
            permissions: { 'cs-api': ['customer_issue_read', 'scenario_read'] },
        },
        {
            icon: PieChartIcon,
            path: 'statistics',
            permissions: {
                'cs-api': [
                    apmsPermissions.fetchApms,
                    companiesPermissions.fetchCompanies,
                    jiraStatisticsPermissions.fetchStatisticsValues,
                    jiraStatisticsPermissions.fetchStatistics,
                ],
            },
        },
        {
            permissions: { 'cs-web': ['admin_panel'] },
            textKey: 'page.admin',
        },
    ],
    [
        {
            icon: BusinessIcon,
            path: 'companies',
            permissions: {
                'cs-api': ['company_read'],
                'cs-web': ['admin_panel'],
            },
        },
        {
            icon: NotificationsIcon,
            path: 'notifications',
            permissions: {
                'cs-api': ['notification_template_read'],
                'cs-web': ['admin_panel'],
            },
        },
    ],
    [
        {
            icon: ClusterIcon,
            path: 'clusters',
            permissions: {
                'cs-api': ['cluster_read'],
                'cs-web': ['admin_panel'],
            },
        },
        {
            icon: SettingsIcon,
            path: 'servers',
            permissions: {
                'cs-api': ['server_read'],
                'cs-web': ['admin_panel'],
            },
        },
    ],
    [
        {
            icon: DnsIcon,
            path: 'environments',
            permissions: {
                'cs-api': ['apm_read', 'company_read'],
                'cs-web': ['admin_panel'],
            },
        },
    ],
    [
        {
            icon: ReleaseNoteIcon,
            path: 'release-notes',
            permissions: {
                'cs-api': ['release_note_read'],
                'cs-web': ['admin_panel'],
            },
        },
    ],
];

export const COUNTRIES = {
    AD: defineMessage({
        defaultMessage: 'Andorra',
        id: 'constants.countries.andorra',
    }).id,
    AE: defineMessage({
        defaultMessage: 'United Arab Emirates',
        id: 'constants.countries.unitedArabEmirates',
    }).id,
    AF: defineMessage({ defaultMessage: 'Afghanistan', id: 'constants.countries.afghanistan' }).id,
    AG: defineMessage({
        defaultMessage: 'Antigua and Barbuda',
        id: 'constants.countries.antiguaAndBarbuda',
    }).id,
    AI: defineMessage({ defaultMessage: 'Anguilla', id: 'constants.countries.anguilla' }).id,
    AL: defineMessage({ defaultMessage: 'Albania', id: 'constants.countries.albania' }).id,
    AM: defineMessage({ defaultMessage: 'Armenia', id: 'constants.countries.armenia' }).id,
    AO: defineMessage({ defaultMessage: 'Angola', id: 'constants.countries.angola' }).id,
    AQ: defineMessage({ defaultMessage: 'Antarctica', id: 'constants.countries.antarctica' }).id,
    AR: defineMessage({ defaultMessage: 'Argentina', id: 'constants.countries.argentina' }).id,
    AS: defineMessage({ defaultMessage: 'American Samoa', id: 'constants.countries.americanSamoa' })
        .id,
    AT: defineMessage({ defaultMessage: 'Austria', id: 'constants.countries.austria' }).id,
    AU: defineMessage({ defaultMessage: 'Australia', id: 'constants.countries.australia' }).id,
    AW: defineMessage({ defaultMessage: 'Aruba', id: 'constants.countries.aruba' }).id,
    AX: defineMessage({ defaultMessage: 'Aland Islands', id: 'constants.countries.alandIslands' })
        .id,
    AZ: defineMessage({ defaultMessage: 'Azerbaijan', id: 'constants.countries.azerbaijan' }).id,
    BA: defineMessage({
        defaultMessage: 'Bosnia and Herzegovina',
        id: 'constants.countries.bosniaAndHerzegovina',
    }).id,
    BB: defineMessage({ defaultMessage: 'Barbados', id: 'constants.countries.barbados' }).id,
    BD: defineMessage({ defaultMessage: 'Bangladesh', id: 'constants.countries.bangladesh' }).id,
    BE: defineMessage({ defaultMessage: 'Belgium', id: 'constants.countries.belgium' }).id,
    BF: defineMessage({ defaultMessage: 'Burkina Faso', id: 'constants.countries.burkinaFaso' }).id,
    BG: defineMessage({ defaultMessage: 'Bulgaria', id: 'constants.countries.bulgaria' }).id,
    BH: defineMessage({ defaultMessage: 'Bahrain', id: 'constants.countries.bahrain' }).id,
    BI: defineMessage({ defaultMessage: 'Burundi', id: 'constants.countries.burundi' }).id,
    BJ: defineMessage({ defaultMessage: 'Benin', id: 'constants.countries.benin' }).id,
    BL: defineMessage({
        defaultMessage: 'Saint Barthelemy',
        id: 'constants.countries.saintBarthelemy',
    }).id,
    BM: defineMessage({ defaultMessage: 'Bermuda', id: 'constants.countries.bermuda' }).id,
    BN: defineMessage({ defaultMessage: 'Brunei', id: 'constants.countries.brunei' }).id,
    BO: defineMessage({ defaultMessage: 'Bolivia', id: 'constants.countries.bolivia' }).id,
    BQ: defineMessage({
        defaultMessage: 'Bonaire, Saint Eustatius and Saba ',
        id: 'constants.countries.bonaireSaintEustatiusAndSaba',
    }).id,
    BR: defineMessage({ defaultMessage: 'Brazil', id: 'constants.countries.brazil' }).id,
    BS: defineMessage({ defaultMessage: 'Bahamas', id: 'constants.countries.bahamas' }).id,
    BT: defineMessage({ defaultMessage: 'Bhutan', id: 'constants.countries.bhutan' }).id,
    BV: defineMessage({ defaultMessage: 'Bouvet Island', id: 'constants.countries.bouvetIsland' })
        .id,
    BW: defineMessage({ defaultMessage: 'Botswana', id: 'constants.countries.botswana' }).id,
    BY: defineMessage({ defaultMessage: 'Belarus', id: 'constants.countries.belarus' }).id,
    BZ: defineMessage({ defaultMessage: 'Belize', id: 'constants.countries.belize' }).id,
    CA: defineMessage({ defaultMessage: 'Canada', id: 'constants.countries.canada' }).id,
    CC: defineMessage({ defaultMessage: 'Cocos Islands', id: 'constants.countries.cocosIslands' })
        .id,
    CD: defineMessage({
        defaultMessage: 'Democratic Republic of the Congo',
        id: 'constants.countries.democraticRepublicOfTheCongo',
    }).id,
    CF: defineMessage({
        defaultMessage: 'Central African Republic',
        id: 'constants.countries.centralAfricanRepublic',
    }).id,
    CG: defineMessage({
        defaultMessage: 'Republic of the Congo',
        id: 'constants.countries.republicOfTheCongo',
    }).id,
    CH: defineMessage({ defaultMessage: 'Switzerland', id: 'constants.countries.switzerland' }).id,
    CI: defineMessage({ defaultMessage: 'Ivory Coast', id: 'constants.countries.ivoryCoast' }).id,
    CK: defineMessage({ defaultMessage: 'Cook Islands', id: 'constants.countries.cookIslands' }).id,
    CL: defineMessage({ defaultMessage: 'Chile', id: 'constants.countries.chile' }).id,
    CM: defineMessage({ defaultMessage: 'Cameroon', id: 'constants.countries.cameroon' }).id,
    CN: defineMessage({ defaultMessage: 'China', id: 'constants.countries.china' }).id,
    CO: defineMessage({ defaultMessage: 'Colombia', id: 'constants.countries.colombia' }).id,
    CR: defineMessage({ defaultMessage: 'Costa Rica', id: 'constants.countries.costaRica' }).id,
    CU: defineMessage({ defaultMessage: 'Cuba', id: 'constants.countries.cuba' }).id,
    CV: defineMessage({ defaultMessage: 'Cape Verde', id: 'constants.countries.capeVerde' }).id,
    CW: defineMessage({ defaultMessage: 'Curacao', id: 'constants.countries.curacao' }).id,
    CX: defineMessage({
        defaultMessage: 'Christmas Island',
        id: 'constants.countries.christmasIsland',
    }).id,
    CY: defineMessage({ defaultMessage: 'Cyprus', id: 'constants.countries.cyprus' }).id,
    CZ: defineMessage({ defaultMessage: 'Czech Republic', id: 'constants.countries.czechRepublic' })
        .id,
    DE: defineMessage({ defaultMessage: 'Germany', id: 'constants.countries.germany' }).id,
    DJ: defineMessage({ defaultMessage: 'Djibouti', id: 'constants.countries.djibouti' }).id,
    DK: defineMessage({ defaultMessage: 'Denmark', id: 'constants.countries.denmark' }).id,
    DM: defineMessage({ defaultMessage: 'Dominica', id: 'constants.countries.dominica' }).id,
    DO: defineMessage({
        defaultMessage: 'Dominican Republic',
        id: 'constants.countries.dominicanRepublic',
    }).id,
    DZ: defineMessage({ defaultMessage: 'Algeria', id: 'constants.countries.algeria' }).id,
    EC: defineMessage({ defaultMessage: 'Ecuador', id: 'constants.countries.ecuador' }).id,
    EE: defineMessage({ defaultMessage: 'Estonia', id: 'constants.countries.estonia' }).id,
    EG: defineMessage({ defaultMessage: 'Egypt', id: 'constants.countries.egypt' }).id,
    EH: defineMessage({ defaultMessage: 'Western Sahara', id: 'constants.countries.westernSahara' })
        .id,
    ER: defineMessage({ defaultMessage: 'Eritrea', id: 'constants.countries.eritrea' }).id,
    ES: defineMessage({ defaultMessage: 'Spain', id: 'constants.countries.spain' }).id,
    ET: defineMessage({ defaultMessage: 'Ethiopia', id: 'constants.countries.ethiopia' }).id,
    FI: defineMessage({ defaultMessage: 'Finland', id: 'constants.countries.finland' }).id,
    FJ: defineMessage({ defaultMessage: 'Fiji', id: 'constants.countries.fiji' }).id,
    FK: defineMessage({
        defaultMessage: 'Falkland Islands',
        id: 'constants.countries.falklandIslands',
    }).id,
    FM: defineMessage({ defaultMessage: 'Micronesia', id: 'constants.countries.micronesia' }).id,
    FO: defineMessage({ defaultMessage: 'Faroe Islands', id: 'constants.countries.faroeIslands' })
        .id,
    FR: defineMessage({ defaultMessage: 'France', id: 'constants.countries.france' }).id,
    GA: defineMessage({ defaultMessage: 'Gabon', id: 'constants.countries.gabon' }).id,
    GB: defineMessage({ defaultMessage: 'United Kingdom', id: 'constants.countries.unitedKingdom' })
        .id,
    GD: defineMessage({ defaultMessage: 'Grenada', id: 'constants.countries.grenada' }).id,
    GE: defineMessage({ defaultMessage: 'Georgia', id: 'constants.countries.georgia' }).id,
    GF: defineMessage({ defaultMessage: 'French Guiana', id: 'constants.countries.frenchGuiana' })
        .id,
    GG: defineMessage({ defaultMessage: 'Guernsey', id: 'constants.countries.guernsey' }).id,
    GH: defineMessage({ defaultMessage: 'Ghana', id: 'constants.countries.ghana' }).id,
    GI: defineMessage({ defaultMessage: 'Gibraltar', id: 'constants.countries.gibraltar' }).id,
    GL: defineMessage({ defaultMessage: 'Greenland', id: 'constants.countries.greenland' }).id,
    GM: defineMessage({ defaultMessage: 'Gambia', id: 'constants.countries.gambia' }).id,
    GN: defineMessage({ defaultMessage: 'Guinea', id: 'constants.countries.guinea' }).id,
    GP: defineMessage({ defaultMessage: 'Guadeloupe', id: 'constants.countries.guadeloupe' }).id,
    GQ: defineMessage({
        defaultMessage: 'Equatorial Guinea',
        id: 'constants.countries.equatorialGuinea',
    }).id,
    GR: defineMessage({ defaultMessage: 'Greece', id: 'constants.countries.greece' }).id,
    GS: defineMessage({
        defaultMessage: 'South Georgia and the South Sandwich Islands',
        id: 'constants.countries.southGeorgiaAndTheSouthSandwichIslands',
    }).id,
    GT: defineMessage({ defaultMessage: 'Guatemala', id: 'constants.countries.guatemala' }).id,
    GU: defineMessage({ defaultMessage: 'Guam', id: 'constants.countries.guam' }).id,
    GW: defineMessage({ defaultMessage: 'Guinea-Bissau', id: 'constants.countries.guineaBissau' })
        .id,
    GY: defineMessage({ defaultMessage: 'Guyana', id: 'constants.countries.guyana' }).id,
    HK: defineMessage({ defaultMessage: 'Hong Kong', id: 'constants.countries.hongKong' }).id,
    HM: defineMessage({
        defaultMessage: 'Heard Island and McDonald Islands',
        id: 'constants.countries.heardIslandAndMcDonaldIslands',
    }).id,
    HN: defineMessage({ defaultMessage: 'Honduras', id: 'constants.countries.honduras' }).id,
    HR: defineMessage({ defaultMessage: 'Croatia', id: 'constants.countries.croatia' }).id,
    HT: defineMessage({ defaultMessage: 'Haiti', id: 'constants.countries.haiti' }).id,
    HU: defineMessage({ defaultMessage: 'Hungary', id: 'constants.countries.hungary' }).id,
    ID: defineMessage({ defaultMessage: 'Indonesia', id: 'constants.countries.indonesia' }).id,
    IE: defineMessage({ defaultMessage: 'Ireland', id: 'constants.countries.ireland' }).id,
    IL: defineMessage({ defaultMessage: 'Israel', id: 'constants.countries.israel' }).id,
    IM: defineMessage({ defaultMessage: 'Isle of Man', id: 'constants.countries.isleOfMan' }).id,
    IN: defineMessage({ defaultMessage: 'India', id: 'constants.countries.india' }).id,
    IO: defineMessage({
        defaultMessage: 'British Indian Ocean Territory',
        id: 'constants.countries.britishIndianOceanTerritory',
    }).id,
    IQ: defineMessage({ defaultMessage: 'Iraq', id: 'constants.countries.iraq' }).id,
    IR: defineMessage({ defaultMessage: 'Iran', id: 'constants.countries.iran' }).id,
    IS: defineMessage({ defaultMessage: 'Iceland', id: 'constants.countries.iceland' }).id,
    IT: defineMessage({ defaultMessage: 'Italy', id: 'constants.countries.italy' }).id,
    JE: defineMessage({ defaultMessage: 'Jersey', id: 'constants.countries.jersey' }).id,
    JM: defineMessage({ defaultMessage: 'Jamaica', id: 'constants.countries.jamaica' }).id,
    JO: defineMessage({ defaultMessage: 'Jordan', id: 'constants.countries.jordan' }).id,
    JP: defineMessage({ defaultMessage: 'Japan', id: 'constants.countries.japan' }).id,
    KE: defineMessage({ defaultMessage: 'Kenya', id: 'constants.countries.kenya' }).id,
    KG: defineMessage({ defaultMessage: 'Kyrgyzstan', id: 'constants.countries.kyrgyzstan' }).id,
    KH: defineMessage({ defaultMessage: 'Cambodia', id: 'constants.countries.cambodia' }).id,
    KI: defineMessage({ defaultMessage: 'Kiribati', id: 'constants.countries.kiribati' }).id,
    KM: defineMessage({ defaultMessage: 'Comoros', id: 'constants.countries.comoros' }).id,
    KN: defineMessage({
        defaultMessage: 'Saint Kitts and Nevis',
        id: 'constants.countries.saintKittsAndNevis',
    }).id,
    KP: defineMessage({ defaultMessage: 'North Korea', id: 'constants.countries.northKorea' }).id,
    KR: defineMessage({ defaultMessage: 'South Korea', id: 'constants.countries.southKorea' }).id,
    KW: defineMessage({ defaultMessage: 'Kuwait', id: 'constants.countries.kuwait' }).id,
    KY: defineMessage({ defaultMessage: 'Cayman Islands', id: 'constants.countries.caymanIslands' })
        .id,
    KZ: defineMessage({ defaultMessage: 'Kazakhstan', id: 'constants.countries.kazakhstan' }).id,
    LA: defineMessage({ defaultMessage: 'Laos', id: 'constants.countries.laos' }).id,
    LB: defineMessage({ defaultMessage: 'Lebanon', id: 'constants.countries.lebanon' }).id,
    LC: defineMessage({ defaultMessage: 'Saint Lucia', id: 'constants.countries.saintLucia' }).id,
    LI: defineMessage({ defaultMessage: 'Liechtenstein', id: 'constants.countries.liechtenstein' })
        .id,
    LK: defineMessage({ defaultMessage: 'Sri Lanka', id: 'constants.countries.sriLanka' }).id,
    LR: defineMessage({ defaultMessage: 'Liberia', id: 'constants.countries.liberia' }).id,
    LS: defineMessage({ defaultMessage: 'Lesotho', id: 'constants.countries.lesotho' }).id,
    LT: defineMessage({ defaultMessage: 'Lithuania', id: 'constants.countries.lithuania' }).id,
    LU: defineMessage({ defaultMessage: 'Luxembourg', id: 'constants.countries.luxembourg' }).id,
    LV: defineMessage({ defaultMessage: 'Latvia', id: 'constants.countries.latvia' }).id,
    LY: defineMessage({ defaultMessage: 'Libya', id: 'constants.countries.libya' }).id,
    MA: defineMessage({ defaultMessage: 'Morocco', id: 'constants.countries.morocco' }).id,
    MC: defineMessage({ defaultMessage: 'Monaco', id: 'constants.countries.monaco' }).id,
    MD: defineMessage({ defaultMessage: 'Moldova', id: 'constants.countries.moldova' }).id,
    ME: defineMessage({ defaultMessage: 'Montenegro', id: 'constants.countries.montenegro' }).id,
    MF: defineMessage({ defaultMessage: 'Saint Martin', id: 'constants.countries.saintMartin' }).id,
    MG: defineMessage({ defaultMessage: 'Madagascar', id: 'constants.countries.madagascar' }).id,
    MH: defineMessage({
        defaultMessage: 'Marshall Islands',
        id: 'constants.countries.marshallIslands',
    }).id,
    MK: defineMessage({ defaultMessage: 'Macedonia', id: 'constants.countries.macedonia' }).id,
    ML: defineMessage({ defaultMessage: 'Mali', id: 'constants.countries.mali' }).id,
    MM: defineMessage({ defaultMessage: 'Myanmar', id: 'constants.countries.myanmar' }).id,
    MN: defineMessage({ defaultMessage: 'Mongolia', id: 'constants.countries.mongolia' }).id,
    MO: defineMessage({ defaultMessage: 'Macao', id: 'constants.countries.macao' }).id,
    MP: defineMessage({
        defaultMessage: 'Northern Mariana Islands',
        id: 'constants.countries.northernMarianaIslands',
    }).id,
    MQ: defineMessage({ defaultMessage: 'Martinique', id: 'constants.countries.martinique' }).id,
    MR: defineMessage({ defaultMessage: 'Mauritania', id: 'constants.countries.mauritania' }).id,
    MS: defineMessage({ defaultMessage: 'Montserrat', id: 'constants.countries.montserrat' }).id,
    MT: defineMessage({ defaultMessage: 'Malta', id: 'constants.countries.malta' }).id,
    MU: defineMessage({ defaultMessage: 'Mauritius', id: 'constants.countries.mauritius' }).id,
    MV: defineMessage({ defaultMessage: 'Maldives', id: 'constants.countries.maldives' }).id,
    MW: defineMessage({ defaultMessage: 'Malawi', id: 'constants.countries.malawi' }).id,
    MX: defineMessage({ defaultMessage: 'Mexico', id: 'constants.countries.mexico' }).id,
    MY: defineMessage({ defaultMessage: 'Malaysia', id: 'constants.countries.malaysia' }).id,
    MZ: defineMessage({ defaultMessage: 'Mozambique', id: 'constants.countries.mozambique' }).id,
    NA: defineMessage({ defaultMessage: 'Namibia', id: 'constants.countries.namibia' }).id,
    NC: defineMessage({ defaultMessage: 'New Caledonia', id: 'constants.countries.newCaledonia' })
        .id,
    NE: defineMessage({ defaultMessage: 'Niger', id: 'constants.countries.niger' }).id,
    NF: defineMessage({ defaultMessage: 'Norfolk Island', id: 'constants.countries.norfolkIsland' })
        .id,
    NG: defineMessage({ defaultMessage: 'Nigeria', id: 'constants.countries.nigeria' }).id,
    NI: defineMessage({ defaultMessage: 'Nicaragua', id: 'constants.countries.nicaragua' }).id,
    NL: defineMessage({ defaultMessage: 'Netherlands', id: 'constants.countries.netherlands' }).id,
    NO: defineMessage({ defaultMessage: 'Norway', id: 'constants.countries.norway' }).id,
    NP: defineMessage({ defaultMessage: 'Nepal', id: 'constants.countries.nepal' }).id,
    NR: defineMessage({ defaultMessage: 'Nauru', id: 'constants.countries.nauru' }).id,
    NU: defineMessage({ defaultMessage: 'Niue', id: 'constants.countries.niue' }).id,
    NZ: defineMessage({ defaultMessage: 'New Zealand', id: 'constants.countries.newZealand' }).id,
    OM: defineMessage({ defaultMessage: 'Oman', id: 'constants.countries.oman' }).id,
    PA: defineMessage({ defaultMessage: 'Panama', id: 'constants.countries.panama' }).id,
    PE: defineMessage({ defaultMessage: 'Peru', id: 'constants.countries.peru' }).id,
    PF: defineMessage({
        defaultMessage: 'French Polynesia',
        id: 'constants.countries.frenchPolynesia',
    }).id,
    PG: defineMessage({
        defaultMessage: 'Papua New Guinea',
        id: 'constants.countries.papuaNewGuinea',
    }).id,
    PH: defineMessage({ defaultMessage: 'Philippines', id: 'constants.countries.philippines' }).id,
    PK: defineMessage({ defaultMessage: 'Pakistan', id: 'constants.countries.pakistan' }).id,
    PL: defineMessage({ defaultMessage: 'Poland', id: 'constants.countries.poland' }).id,
    PM: defineMessage({
        defaultMessage: 'Saint Pierre and Miquelon',
        id: 'constants.countries.saintPierreAndMiquelon',
    }).id,
    PN: defineMessage({ defaultMessage: 'Pitcairn', id: 'constants.countries.pitcairn' }).id,
    PR: defineMessage({ defaultMessage: 'Puerto Rico', id: 'constants.countries.puertoRico' }).id,
    PS: defineMessage({
        defaultMessage: 'Palestinian Territory',
        id: 'constants.countries.palestinianTerritory',
    }).id,
    PT: defineMessage({ defaultMessage: 'Portugal', id: 'constants.countries.portugal' }).id,
    PW: defineMessage({ defaultMessage: 'Palau', id: 'constants.countries.palau' }).id,
    PY: defineMessage({ defaultMessage: 'Paraguay', id: 'constants.countries.paraguay' }).id,
    QA: defineMessage({ defaultMessage: 'Qatar', id: 'constants.countries.qatar' }).id,
    RE: defineMessage({ defaultMessage: 'Reunion', id: 'constants.countries.reunion' }).id,
    RO: defineMessage({ defaultMessage: 'Romania', id: 'constants.countries.romania' }).id,
    RS: defineMessage({ defaultMessage: 'Serbia', id: 'constants.countries.serbia' }).id,
    RU: defineMessage({ defaultMessage: 'Russia', id: 'constants.countries.russia' }).id,
    RW: defineMessage({ defaultMessage: 'Rwanda', id: 'constants.countries.rwanda' }).id,
    SA: defineMessage({ defaultMessage: 'Saudi Arabia', id: 'constants.countries.saudiArabia' }).id,
    SB: defineMessage({
        defaultMessage: 'Solomon Islands',
        id: 'constants.countries.solomonIslands',
    }).id,
    SC: defineMessage({ defaultMessage: 'Seychelles', id: 'constants.countries.seychelles' }).id,
    SD: defineMessage({ defaultMessage: 'Sudan', id: 'constants.countries.sudan' }).id,
    SE: defineMessage({ defaultMessage: 'Sweden', id: 'constants.countries.sweden' }).id,
    SG: defineMessage({ defaultMessage: 'Singapore', id: 'constants.countries.singapore' }).id,
    SH: defineMessage({ defaultMessage: 'Saint Helena', id: 'constants.countries.saintHelena' }).id,
    SI: defineMessage({ defaultMessage: 'Slovenia', id: 'constants.countries.slovenia' }).id,
    SJ: defineMessage({
        defaultMessage: 'Svalbard and Jan Mayen',
        id: 'constants.countries.svalbardAndJanMayen',
    }).id,
    SK: defineMessage({ defaultMessage: 'Slovakia', id: 'constants.countries.slovakia' }).id,
    SL: defineMessage({ defaultMessage: 'Sierra Leone', id: 'constants.countries.sierraLeone' }).id,
    SM: defineMessage({ defaultMessage: 'San Marino', id: 'constants.countries.sanMarino' }).id,
    SN: defineMessage({ defaultMessage: 'Senegal', id: 'constants.countries.senegal' }).id,
    SO: defineMessage({ defaultMessage: 'Somalia', id: 'constants.countries.somalia' }).id,
    SR: defineMessage({ defaultMessage: 'Suriname', id: 'constants.countries.suriname' }).id,
    SS: defineMessage({ defaultMessage: 'South Sudan', id: 'constants.countries.southSudan' }).id,
    ST: defineMessage({
        defaultMessage: 'Sao Tome and Principe',
        id: 'constants.countries.saoTomeAndPrincipe',
    }).id,
    SV: defineMessage({ defaultMessage: 'El Salvador', id: 'constants.countries.elSalvador' }).id,
    SX: defineMessage({ defaultMessage: 'Sint Maarten', id: 'constants.countries.sintMaarten' }).id,
    SY: defineMessage({ defaultMessage: 'Syria', id: 'constants.countries.syria' }).id,
    SZ: defineMessage({ defaultMessage: 'Swaziland', id: 'constants.countries.swaziland' }).id,
    TC: defineMessage({
        defaultMessage: 'Turks and Caicos Islands',
        id: 'constants.countries.turksAndCaicosIslands',
    }).id,
    TD: defineMessage({ defaultMessage: 'Chad', id: 'constants.countries.chad' }).id,
    TF: defineMessage({
        defaultMessage: 'French Southern Territories',
        id: 'constants.countries.frenchSouthernTerritories',
    }).id,
    TG: defineMessage({ defaultMessage: 'Togo', id: 'constants.countries.togo' }).id,
    TH: defineMessage({ defaultMessage: 'Thailand', id: 'constants.countries.thailand' }).id,
    TJ: defineMessage({ defaultMessage: 'Tajikistan', id: 'constants.countries.tajikistan' }).id,
    TK: defineMessage({ defaultMessage: 'Tokelau', id: 'constants.countries.tokelau' }).id,
    TL: defineMessage({ defaultMessage: 'East Timor', id: 'constants.countries.eastTimor' }).id,
    TM: defineMessage({ defaultMessage: 'Turkmenistan', id: 'constants.countries.turkmenistan' })
        .id,
    TN: defineMessage({ defaultMessage: 'Tunisia', id: 'constants.countries.tunisia' }).id,
    TO: defineMessage({ defaultMessage: 'Tonga', id: 'constants.countries.tonga' }).id,
    TR: defineMessage({ defaultMessage: 'Turkey', id: 'constants.countries.turkey' }).id,
    TT: defineMessage({
        defaultMessage: 'Trinidad and Tobago',
        id: 'constants.countries.trinidadAndTobago',
    }).id,
    TV: defineMessage({ defaultMessage: 'Tuvalu', id: 'constants.countries.tuvalu' }).id,
    TW: defineMessage({ defaultMessage: 'Taiwan', id: 'constants.countries.taiwan' }).id,
    TZ: defineMessage({ defaultMessage: 'Tanzania', id: 'constants.countries.tanzania' }).id,
    UA: defineMessage({ defaultMessage: 'Ukraine', id: 'constants.countries.ukraine' }).id,
    UG: defineMessage({ defaultMessage: 'Uganda', id: 'constants.countries.uganda' }).id,
    UM: defineMessage({
        defaultMessage: 'United States Minor Outlying Islands',
        id: 'constants.countries.unitedStatesMinorOutlyingIslands',
    }).id,
    US: defineMessage({ defaultMessage: 'United States', id: 'constants.countries.unitedStates' })
        .id,
    UY: defineMessage({ defaultMessage: 'Uruguay', id: 'constants.countries.uruguay' }).id,
    UZ: defineMessage({ defaultMessage: 'Uzbekistan', id: 'constants.countries.uzbekistan' }).id,
    VA: defineMessage({ defaultMessage: 'Vatican', id: 'constants.countries.vatican' }).id,
    VC: defineMessage({
        defaultMessage: 'Saint Vincent and the Grenadines',
        id: 'constants.countries.saintVincentAndTheGrenadines',
    }).id,
    VE: defineMessage({ defaultMessage: 'Venezuela', id: 'constants.countries.venezuela' }).id,
    VG: defineMessage({
        defaultMessage: 'British Virgin Islands',
        id: 'constants.countries.britishVirginIslands',
    }).id,
    VI: defineMessage({
        defaultMessage: 'U.S. Virgin Islands',
        id: 'constants.countries.usVirginIslands',
    }).id,
    VN: defineMessage({ defaultMessage: 'Vietnam', id: 'constants.countries.vietnam' }).id,
    VU: defineMessage({ defaultMessage: 'Vanuatu', id: 'constants.countries.vanuatu' }).id,
    WF: defineMessage({
        defaultMessage: 'Wallis and Futuna',
        id: 'constants.countries.wallisAndFutuna',
    }).id,
    WS: defineMessage({ defaultMessage: 'Samoa', id: 'constants.countries.samoa' }).id,
    XK: defineMessage({ defaultMessage: 'Kosovo', id: 'constants.countries.kosovo' }).id,
    YE: defineMessage({ defaultMessage: 'Yemen', id: 'constants.countries.yemen' }).id,
    YT: defineMessage({ defaultMessage: 'Mayotte', id: 'constants.countries.mayotte' }).id,
    ZA: defineMessage({ defaultMessage: 'South Africa', id: 'constants.countries.southAfrica' }).id,
    ZM: defineMessage({ defaultMessage: 'Zambia', id: 'constants.countries.zambia' }).id,
    ZW: defineMessage({ defaultMessage: 'Zimbabwe', id: 'constants.countries.zimbabwe' }).id,
};
